import React, { useEffect, useState } from "react";
import CustomModal from "../../components/CustomModal";
import { useUpdateUser } from "./hooks";
import { CONTENT_SECTIONS } from "../../constants/contentSections";
import { useT } from "../../i18n";

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  user: {
    uuid: string;
    username: string;
    email: string;
    userRole: string;
    accountType: string;
    contentAccess?: string[];
  } | null;
}

const userRoles = ["superuser", "user"];

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const t = useT();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [userRole, setUserRole] = useState("user");
  const [contentAccess, setContentAccess] = useState<string[]>([]);
  const [error, setError] = useState("");

  const updateUserMutation = useUpdateUser();

  // Populate the form whenever a new user is opened for editing
  useEffect(() => {
    if (user) {
      setUsername(user.username || "");
      setEmail(user.email || "");
      setUserRole(user.userRole || "user");
      setContentAccess(user.contentAccess || []);
      setError("");
    }
  }, [user]);

  const toggleSection = (key: string) =>
    setContentAccess((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const handleSave = async () => {
    setError("");

    if (!user) return;

    if (!username || !email) {
      setError(t("common.validation.requiredFields"));
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t("common.validation.invalidEmail"));
      return;
    }

    try {
      const data: any = { username, email };

      // Only partner accounts carry a user role
      if (user.accountType === "partner") {
        data.userRole = userRole;
      }

      if (user.accountType === "marketer") {
        if (contentAccess.length === 0) {
          setError(t("users.editUserModal.errors.contentSectionRequired"));
          return;
        }
        data.contentAccess = contentAccess;
      }

      await updateUserMutation.mutateAsync({ uuid: user.uuid, data });

      onClose();
      onSuccess?.();
    } catch (err: any) {
      setError(t("users.editUserModal.errors.updateFailed"));
    }
  };

  const handleClose = () => {
    setError("");
    onClose();
  };

  return (
    <CustomModal open={open} onClose={onClose}>
      <div
        className="w-[820px] max-w-full h-full flex flex-col mx-auto"
        style={{ height: "100%" }}
      >
        <div className="flex flex-col w-full h-full" style={{ height: "100%" }}>
          <div className="flex items-center gap-3 pt-8 pb-2 border-b border-gray-200 w-full pl-0 pr-0">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-lg ml-0"
              style={{ backgroundColor: "#7E3AF220" }}
            >
              <i
                className="fa-solid fa-user-pen text-2xl"
                style={{ color: "#7E3AF2" }}
              ></i>
            </div>
            <div className="ml-0">
              <h2 className="text-xl font-semibold text-gray-900">{t("users.editUserModal.title")}</h2>
              <div className="text-sm text-gray-400 font-medium leading-tight">
                {t("users.editUserModal.subtitle")}
              </div>
            </div>
          </div>
          <form
            className="py-8 space-y-5 w-full flex flex-col justify-center px-0 flex-1"
            style={{ height: "100%" }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("users.editUserModal.usernameLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t("users.editUserModal.usernamePlaceholder")}
                required
              />
            </div>

            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("users.editUserModal.emailLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("users.editUserModal.emailPlaceholder")}
                required
              />
            </div>

            {/* User Role field - Only relevant for partner accounts */}
            {user?.accountType === "partner" && (
              <div className="px-0">
                <label className="block text-sm text-gray-700 mb-1 ml-0">
                  {t("users.editUserModal.userRoleLabel")} <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                  required
                >
                  <option value="">{t("users.editUserModal.userRolePlaceholder")}</option>
                  {userRoles.map((role) => (
                    <option key={role} value={role}>
                      {t(`users.userRoles.${role}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Content sections - Only relevant for marketer accounts */}
            {user?.accountType === "marketer" && (
              <div className="px-0">
                <label className="block text-sm text-gray-700 mb-1 ml-0">
                  {t("users.editUserModal.contentAccessLabel")} <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  {t("users.editUserModal.contentAccessHint")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {CONTENT_SECTIONS.map((section) => {
                    const selected = contentAccess.includes(section.key);
                    return (
                      <button
                        key={section.key}
                        type="button"
                        onClick={() => toggleSection(section.key)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
                          selected
                            ? "border-purple-400 bg-purple-50 text-purple-800"
                            : "border-gray-300 bg-white text-gray-600 hover:border-purple-300"
                        }`}
                      >
                        <i
                          className={`fa-solid ${
                            selected ? "fa-square-check" : "fa-square"
                          } ${selected ? "text-purple-600" : "text-gray-300"}`}
                        ></i>
                        <i className={`fa-solid ${section.icon} w-4 text-center`}></i>
                        {t(`contentSections.${section.key}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {error && (
              <div className="text-red-600 text-sm font-medium mt-2 px-0">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2 px-0">
              <button
                type="button"
                className="px-6 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium"
                onClick={handleClose}
                disabled={updateUserMutation.isPending}
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="px-6 py-2 rounded-lg text-white hover:opacity-90 font-medium flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: "#7E3AF2" }}
                disabled={updateUserMutation.isPending}
              >
                {updateUserMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    {t("common.loading.saving")}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-floppy-disk"></i>
                    {t("users.editUserModal.submit")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </CustomModal>
  );
};

export default EditUserModal;
